import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Mail, MapPin, Phone, ScrollText, User } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import {
  getCustomer,
  getCustomerFinancialSummary,
} from '../api/customers';

/**
 * Customer detail — the field-rep "look up their phone number / where
 * are they / how much do they owe" page.
 *
 * Loads the customer + their financial summary in parallel. Most reps
 * tap a phone number expecting it to dial; the contact card uses
 * `tel:` and `mailto:` hrefs so iOS / Android handle that natively.
 *
 * Edit + delete are deliberately not surfaced here — they're managed
 * on the desktop ERP. Mobile is for capture and reference, not for
 * customer-record curation. If the rep needs to fix a typo they go
 * desktop-side.
 */

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CustomerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const flash = location.state?.flash;

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Financial summary loads in parallel — rep sees contact info
  // immediately, financial card pops in once the second call returns.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSummaryLoading(true);
    setSummaryError(null);

    getCustomer(id)
      .then((data) => { if (!cancelled) setCustomer(data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load customer');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    getCustomerFinancialSummary(id)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((err) => {
        if (cancelled) return;
        // Financial summary is best-effort — a 404 here probably means
        // a brand-new customer with no transactions; we surface it as
        // "no activity yet" rather than a hard error.
        setSummaryError(err.response?.data?.detail || err.message || 'Could not load summary');
      })
      .finally(() => { if (!cancelled) setSummaryLoading(false); });

    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Customer" icon={User} />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (error || !customer) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Customer" icon={User} />
        <p role="alert" className="text-small text-danger">
          {error || 'Customer not found.'}
        </p>
      </main>
    );
  }

  const fullAddress = [customer.street_number, customer.street]
    .filter(Boolean)
    .join(' ')
    .trim() || customer.city;

  return (
    <main className="min-h-full px-4 pb-6">
      <PageHeader
        title={customer.name}
        subtitle={customer.contact_name ?? customer.city ?? '—'}
        icon={User}
      />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Contact card — taps on phone / email use native handlers
          (iOS dials, Android opens the dialer; mailto opens the mail
          composer). This is the most-tapped section of the page on
          a field rep's day-to-day. */}
      <section className="mb-card mb-3 space-y-3">
        {customer.phone ? (
          <a
            href={`tel:${customer.phone.replace(/\s+/g, '')}`}
            className="flex items-center gap-3 no-underline min-h-touch"
          >
            <span className="w-10 h-10 rounded-card bg-blue-50 flex items-center justify-center shrink-0">
              <Phone className="h-5 w-5 text-blue-700" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body text-gray-900">{customer.phone}</p>
              <p className="text-caption text-gray-500">Tap to call</p>
            </div>
          </a>
        ) : (
          <div className="flex items-center gap-3 min-h-touch opacity-50">
            <span className="w-10 h-10 rounded-card bg-gray-100 flex items-center justify-center shrink-0">
              <Phone className="h-5 w-5 text-gray-400" />
            </span>
            <p className="text-small text-gray-500">No phone on record</p>
          </div>
        )}

        {customer.email && (
          <a
            href={`mailto:${customer.email}`}
            className="flex items-center gap-3 no-underline min-h-touch"
          >
            <span className="w-10 h-10 rounded-card bg-indigo-50 flex items-center justify-center shrink-0">
              <Mail className="h-5 w-5 text-indigo-700" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body text-gray-900 truncate">{customer.email}</p>
              <p className="text-caption text-gray-500">Tap to email</p>
            </div>
          </a>
        )}

        {(fullAddress || customer.city) && (
          <div className="flex items-start gap-3 min-h-touch">
            <span className="w-10 h-10 rounded-card bg-green-50 flex items-center justify-center shrink-0 mt-0.5">
              <MapPin className="h-5 w-5 text-green-700" />
            </span>
            <div className="min-w-0 flex-1">
              {fullAddress && (
                <p className="text-body text-gray-900">{fullAddress}</p>
              )}
              {customer.city && (
                <p className="text-small text-gray-600">{customer.city}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Tax IDs — only render if at least one is present, since most
          customers only have one or neither and we don't want an empty
          card. */}
      {(customer.vat_number || customer.tin_number) && (
        <section className="mb-card mb-3 space-y-2">
          {customer.vat_number && (
            <div className="flex justify-between text-small">
              <span className="text-gray-500">VAT number</span>
              <span className="font-mono text-gray-800">{customer.vat_number}</span>
            </div>
          )}
          {customer.tin_number && (
            <div className="flex justify-between text-small">
              <span className="text-gray-500">TIN number</span>
              <span className="font-mono text-gray-800">{customer.tin_number}</span>
            </div>
          )}
        </section>
      )}

      {/* Financial snapshot — loaded in parallel with the customer
          so the contact info renders immediately and this card pops
          in once it lands. Values:
            - invoiced  = total billed across all sales
            - received  = total payments received
            - outstanding = currently owed
            - draft = pending non-delivered sales
          For deeper analysis (per-invoice ledger, aging buckets) the
          rep flips to desktop. */}
      <section className="mb-card mb-3">
        <p className="text-small font-medium text-gray-700 mb-2">Account</p>
        {summaryLoading && (
          <p className="text-small text-gray-500">Loading summary…</p>
        )}
        {!summaryLoading && summaryError && (
          <p className="text-small text-gray-500">No transactions yet</p>
        )}
        {!summaryLoading && summary && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-caption text-gray-500">Invoiced</p>
              <p className="text-title font-semibold text-gray-900">
                {fmt(summary.invoiced)}
              </p>
            </div>
            <div>
              <p className="text-caption text-gray-500">Received</p>
              <p className="text-title font-semibold text-gray-900">
                {fmt(summary.received)}
              </p>
            </div>
            <div>
              <p className="text-caption text-gray-500">Outstanding</p>
              <p
                className={`text-title font-semibold ${
                  Number(summary.outstanding) > 0
                    ? 'text-amber-700'
                    : 'text-gray-900'
                }`}
              >
                {fmt(summary.outstanding)}
              </p>
            </div>
            <div>
              <p className="text-caption text-gray-500">Draft</p>
              <p className="text-title font-semibold text-gray-900">
                {fmt(summary.draft)}
              </p>
            </div>
          </div>
        )}
      </section>

      {customer.notes && (
        <section className="mb-card mb-3">
          <div className="flex items-start gap-2 mb-1">
            <ScrollText className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
            <p className="text-small font-medium text-gray-700">Notes</p>
          </div>
          <p className="text-small text-gray-700 whitespace-pre-wrap">
            {customer.notes}
          </p>
        </section>
      )}
    </main>
  );
}
